import { CartUpdateEvent } from '@theme/events';

const KEY = 'overcut:wishlist';

/**
 * Signale un ajout ou un retrait des favoris aux outils de mesure.
 *
 * Deux canaux, parce que les deux installations de GA4 coexistent sur Shopify :
 *  - Shopify.analytics.publish : le seul chemin qui atteint un pixel
 *    personnalise, ou tourne GA4 quand il est pose par l application officielle
 *    Google. Ce bac a sable n expose PAS window.gtag au theme.
 *  - window.gtag : present uniquement si l etiquette Google est collee a la
 *    main dans le theme.
 *
 * Aucun des deux n est requis : sans outil installe la fonction ne fait rien,
 * et le coeur continue de fonctionner normalement.
 *
 * @param {string} nom - add_to_wishlist ou remove_from_wishlist
 * @param {WishlistItem} item
 */
function mesurer(nom, item) {
  // Le prix est stocke en texte formate (« 24,90 € ») : GA4 attend un nombre.
  const brut = String(item.price || '').replace(/[^0-9,.-]/g, '').replace(',', '.');
  const valeur = Number(brut) || 0;
  const charge = {
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
    value: valeur,
    items: [
      {
        item_id: String(item.id || ''),
        item_name: item.title || '',
        item_variant: item.handle || '',
        price: valeur,
        quantity: 1,
      },
    ],
  };

  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(nom, charge);
    }
  } catch {
    /* pixel indisponible : la mesure ne doit jamais bloquer le coeur */
  }

  try {
    if (typeof window.gtag === 'function') window.gtag('event', nom, charge);
  } catch {
    /* idem */
  }
}
const CHANGE = 'wishlist:change';

/** @typedef {{id: string, handle: string, title: string, price: string, image: string, url: string}} WishlistItem */

class WishlistStore {
  /** @returns {WishlistItem[]} */
  read() {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {WishlistItem[]} items */
  write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* quota or private mode: keep the in-memory list only */
    }
    document.dispatchEvent(new CustomEvent(CHANGE, { detail: { items }, bubbles: true }));
  }

  /** @param {string} id */
  has(id) {
    return this.read().some((item) => String(item.id) === String(id));
  }

  get count() {
    return this.read().length;
  }

  /** @param {WishlistItem} item @returns {boolean} true when the item is now saved */
  toggle(item) {
    const items = this.read();
    const index = items.findIndex((entry) => String(entry.id) === String(item.id));

    if (index > -1) {
      items.splice(index, 1);
      this.write(items);
      return false;
    }

    items.unshift(item);
    this.write(items);
    return true;
  }

  /** @param {string} id */
  remove(id) {
    this.write(this.read().filter((item) => String(item.id) !== String(id)));
  }

  /**
   * Merges handles saved on the customer metafield into the local list.
   * Write-back to the metafield needs an app proxy, see the theme README.
   * @param {string[]} handles
   */
  async hydrate(handles) {
    if (!Array.isArray(handles) || handles.length === 0) return;

    const known = new Set(this.read().map((item) => item.handle));
    const missing = handles.filter((handle) => handle && !known.has(handle));
    if (missing.length === 0) return;

    const fetched = await Promise.all(missing.map((handle) => fetchProduct(handle)));
    const items = this.read();

    for (const product of fetched) {
      if (product) items.push(toItem(product));
    }

    this.write(items);
  }
}

export const wishlist = new WishlistStore();

/** @param {string} handle */
async function fetchProduct(handle) {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/** @param {Record<string, any>} product */
function toItem(product) {
  return {
    id: String(product.id),
    handle: product.handle,
    title: product.title,
    price: formatMoney(product.price),
    image: product.featured_image || product.images?.[0] || '',
    url: product.url || `/products/${product.handle}`,
  };
}

/** @param {number} cents */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return `${amount} ${window.Shopify?.currency?.active === 'EUR' ? '€' : window.Shopify?.currency?.active || ''}`.trim();
}

/**
 * Heart toggle rendered on product cards and on the product page.
 * Reads the product payload from its own data attributes so the wishlist
 * keeps working with no extra request.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.onClick = this.onClick.bind(this);
    this.sync = this.sync.bind(this);

    this.button.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE, this.sync);
  }

  get item() {
    return {
      id: String(this.dataset.productId),
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      price: this.dataset.productPrice || '',
      image: this.dataset.productImage || '',
      url: this.dataset.productUrl || '',
    };
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const saved = wishlist.toggle(this.item);
    mesurer(saved ? 'add_to_wishlist' : 'remove_from_wishlist', this.item);
    if (saved) {
      this.button?.classList.remove('wishlist-button--beat');
      // force a reflow so the animation replays on every save
      void this.offsetWidth;
      this.button?.classList.add('wishlist-button--beat');
    }
  }

  sync() {
    const saved = wishlist.has(this.dataset.productId || '');
    this.button?.setAttribute('aria-pressed', String(saved));
    this.button?.setAttribute('aria-label', saved ? this.dataset.labelRemove || '' : this.dataset.labelAdd || '');
    this.classList.toggle('wishlist-button-wrapper--saved', saved);
  }
}

/** Count badge sitting next to the header wishlist icon. */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.sync = this.sync.bind(this);
    document.addEventListener(CHANGE, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE, this.sync);
  }

  sync() {
    const count = wishlist.count;
    this.textContent = count > 99 ? '99+' : String(count);
    this.hidden = count === 0;
  }
}

/** Slide-in panel listing the saved products. */
class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.heading = this.querySelector('[data-wishlist-heading-count]');
    this.template = this.querySelector('template');

    this.close = this.close.bind(this);
    this.render = this.render.bind(this);
    this.onOpenRequest = this.onOpenRequest.bind(this);

    document.addEventListener(CHANGE, this.render);
    document.addEventListener('wishlist:open', this.onOpenRequest);

    this.querySelectorAll('[data-wishlist-close]').forEach((node) => node.addEventListener('click', this.close));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    const seed = document.getElementById('wishlist-customer-seed');
    if (seed?.textContent) {
      try {
        wishlist.hydrate(JSON.parse(seed.textContent));
      } catch {
        /* malformed metafield: ignore and keep the local list */
      }
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE, this.render);
    document.removeEventListener('wishlist:open', this.onOpenRequest);
  }

  onOpenRequest() {
    this.open();
  }

  open() {
    this.dialog?.showModal();
    document.body.setAttribute('data-wishlist-open', 'true');
  }

  close() {
    this.dialog?.close();
    document.body.removeAttribute('data-wishlist-open');
  }

  render() {
    const items = wishlist.read();
    if (this.heading) this.heading.textContent = String(items.length);
    if (this.empty) this.empty.hidden = items.length > 0;
    if (!this.list || !this.template) return;

    this.list.replaceChildren();

    for (const item of items) {
      const node = /** @type {HTMLElement} */ (this.template.content.firstElementChild?.cloneNode(true));
      if (!node) continue;

      const image = node.querySelector('img');
      const link = node.querySelectorAll('a');
      const title = node.querySelector('[data-wishlist-title]');
      const price = node.querySelector('[data-wishlist-price]');
      const remove = node.querySelector('[data-wishlist-remove]');
      const add = node.querySelector('[data-wishlist-add]');

      if (image && item.image) {
        image.src = item.image;
        image.alt = item.title;
      }
      link.forEach((anchor) => anchor.setAttribute('href', item.url));
      if (title) title.textContent = item.title;
      if (price) price.textContent = item.price;

      remove?.addEventListener('click', () => wishlist.remove(item.id));
      add?.addEventListener('click', () => this.addToCart(item, /** @type {HTMLButtonElement} */ (add)));

      this.list.append(node);
    }
  }

  /** @param {WishlistItem} item @param {HTMLButtonElement} button */
  addToCart(item, button) {
    return addItemToCart(item, button, 'wishlist-drawer');
  }
}

/**
 * One click straight to the cart when the product has a single available
 * variant, otherwise send the customer to the product page to choose.
 * Shared by the drawer and the wishlist page.
 *
 * @param {WishlistItem} item
 * @param {HTMLButtonElement} button
 * @param {string} sourceId
 */
async function addItemToCart(item, button, sourceId) {
  const product = await fetchProduct(item.handle);
  if (!product) {
    // Produit supprime ou depublie : on purge le favori plutot que de laisser
    // un bouton qui ne fera jamais rien.
    wishlist.remove(item.id);
    return;
  }

  const available = product.variants.filter((variant) => variant.available);

  if (available.length !== 1) {
    window.location.href = item.url;
    return;
  }

  const label = button.textContent;
  button.disabled = true;
  const root = window.Shopify?.routes?.root || '/';

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: available[0].id, quantity: 1 }] }),
    });

    // fetch ne rejette pas sur un 422 : sans ce test, une rupture de stock
    // affichait quand meme "Ajoute" alors que rien n'entrait dans le panier.
    if (!response.ok) throw new Error('add failed');
  } catch {
    button.textContent = button.dataset.labelError || label;
    button.disabled = false;
    setTimeout(() => {
      button.textContent = label;
    }, 1800);
    return;
  }

  // La relecture du panier est volontairement hors du try d'ajout : une coupure
  // reseau ici ne doit pas faire croire a un echec, la ligne est deja au panier.
  let cart = null;
  try {
    const res = await fetch(`${root}cart.js`);
    if (res.ok) cart = await res.json();
  } catch {
    /* pastille rafraichie au prochain chargement */
  }

  document.dispatchEvent(
    new CartUpdateEvent(cart ?? undefined, sourceId, { itemCount: cart?.item_count ?? currentCartCount() + 1 })
  );

  button.textContent = button.dataset.labelAdded || label;
  setTimeout(() => {
    button.textContent = label;
    button.disabled = false;
  }, 1800);
}

/** Lit la pastille panier de l'en-tete, pour ne pas la remettre a zero si /cart.js echoue. */
function currentCartCount() {
  const el = document.querySelector('cart-icon .cart-bubble__text-count');
  return parseInt(el?.textContent?.trim() || '0', 10) || 0;
}

/**
 * Full-page wishlist. Same store as the drawer, laid out as a product grid so
 * the list can be browsed, and linked to, rather than only glanced at.
 */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.template = this.querySelector('template');

    this.render = this.render.bind(this);
    document.addEventListener(CHANGE, this.render);

    const seed = document.getElementById('wishlist-customer-seed');
    if (seed?.textContent) {
      try {
        wishlist.hydrate(JSON.parse(seed.textContent));
      } catch {
        /* malformed metafield: ignore and keep the local list */
      }
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE, this.render);
  }

  render() {
    const items = wishlist.read();

    if (this.countEl) this.countEl.textContent = String(items.length);
    if (this.empty) this.empty.hidden = items.length > 0;
    if (this.grid) this.grid.hidden = items.length === 0;
    if (!this.grid || !this.template) return;

    this.grid.replaceChildren();

    for (const item of items) {
      const node = /** @type {HTMLElement} */ (this.template.content.firstElementChild?.cloneNode(true));
      if (!node) continue;

      const image = node.querySelector('img');
      const title = node.querySelector('[data-wishlist-title]');
      const price = node.querySelector('[data-wishlist-price]');
      const remove = node.querySelector('[data-wishlist-remove]');
      const add = node.querySelector('[data-wishlist-add]');

      if (image && item.image) {
        image.src = item.image;
        image.alt = item.title;
      }
      node.querySelectorAll('a').forEach((anchor) => anchor.setAttribute('href', item.url));
      if (title) title.textContent = item.title;
      if (price) price.textContent = item.price;

      remove?.addEventListener('click', () => wishlist.remove(item.id));
      add?.addEventListener('click', () => addItemToCart(item, /** @type {HTMLButtonElement} */ (add), 'wishlist-page'));

      this.grid.append(node);
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
